(() => {
  if (customElements.get('vm-sticky-atc')) return;

  const DESKTOP = window.matchMedia('(min-width: 700px)');

  class VmStickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = this.findForm();
      if (!this.form) return;

      this.variants = this.readVariants();
      this.mainButton = this.findMainButton();
      this.button = this.querySelector('[data-vm-button]');
      this.priceEl = this.querySelector('[data-vm-price]');
      this.compareEl = this.querySelector('[data-vm-compare]');
      this.badgeEl = this.querySelector('[data-vm-badge]');
      this.savingEl = this.querySelector('[data-vm-saving]');
      this.imageEl = this.querySelector('[data-vm-image]');
      this.header = this.findHeader();

      this.visible = false;
      this.ticking = false;
      this.lastTop = null;

      this.button?.addEventListener('click', this.onAdd);
      document.addEventListener('variant:change', this.onVariantChange);
      this.form.addEventListener('change', this.onFormChange);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      // Show the bar only after the main Add to cart button has scrolled past the top
      this.observer = new IntersectionObserver(([entry]) => {
        this.toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.observer.observe(this.mainButton || this.form);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.button?.removeEventListener('click', this.onAdd);
      document.removeEventListener('variant:change', this.onVariantChange);
      this.form?.removeEventListener('change', this.onFormChange);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    /* ---------- Finding the theme's elements ---------- */

    findForm() {
      const scope = document.querySelector('.shopify-section--main-product') || document;
      return scope.querySelector('form[action*="/cart/add"]');
    }

    findMainButton() {
      const inside = this.form.querySelector('button[type="submit"], input[type="submit"]');
      if (inside) return inside;
      if (!this.form.id) return null;

      return (
        Array.from(document.querySelectorAll('button[type="submit"]')).find(
          (btn) => btn.getAttribute('form') === this.form.id && !btn.closest('product-sticky-bar, vm-sticky-atc')
        ) || null
      );
    }

    findHeader() {
      const section = document.querySelector('.shopify-section--header');
      return section?.querySelector('header') || section || document.querySelector('header');
    }

    readVariants() {
      try {
        return JSON.parse(this.querySelector('[data-vm-variants]')?.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    /* ---------- Add to cart ---------- */

    onAdd = () => {
      if (!this.button || this.button.disabled) return;

      // Use the theme's own button so the cart drawer / notifications work as usual
      if (this.mainButton) {
        if (!this.mainButton.disabled) this.mainButton.click();
        return;
      }

      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit();
      } else {
        this.form.submit();
      }
    };

    /* ---------- Variant changes ---------- */

    onVariantChange = (event) => {
      const id = event.detail?.variant?.id;
      if (id) this.update(String(id));
    };

    onFormChange = () => {
      requestAnimationFrame(() => {
        const input = this.form.querySelector('[name="id"]');
        if (input) this.update(String(input.value));
      });
    };

    update(id) {
      const data = this.variants[id];
      if (!data) return;

      const onSale = Boolean(data.compare);

      if (this.priceEl) this.priceEl.textContent = data.price;

      if (this.compareEl) {
        this.compareEl.hidden = !onSale;
        this.compareEl.textContent = onSale ? data.compare : '';
      }

      if (this.badgeEl) {
        this.badgeEl.hidden = !onSale;
        if (this.savingEl) this.savingEl.textContent = data.saving;
      }

      if (this.imageEl && data.image) this.imageEl.src = data.image;

      if (this.button) {
        this.button.disabled = !data.available;
        this.button.textContent = data.available ? this.dataset.buttonText : this.dataset.soldOutText;
      }
    }

    /* ---------- Show / hide and position ---------- */

    toggle(show) {
      if (show === this.visible) return;
      this.visible = show;

      if (show) this.updateTop();

      this.classList.toggle('is-visible', show);
      this.inert = !show;
      this.setAttribute('aria-hidden', String(!show));
    }

    onScroll = () => {
      if (!this.visible || this.ticking) return;
      this.ticking = true;

      requestAnimationFrame(() => {
        this.ticking = false;
        this.updateTop();
      });
    };

    // On desktop (top position), sit directly below the header, even if the header hides on scroll
    updateTop() {
      if (this.dataset.desktopPosition !== 'top' || !DESKTOP.matches || !this.header) return;

      const top = Math.max(0, Math.round(this.header.getBoundingClientRect().bottom));
      if (top === this.lastTop) return;

      this.lastTop = top;
      this.style.setProperty('--vm-top', `${top}px`);
    }
  }

  customElements.define('vm-sticky-atc', VmStickyAtc);
})();